import { c as _c } from "react/compiler-runtime";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { ConfigProvider as PhoneConfigProvider } from 'antd-country-phone-input';
import 'antd-country-phone-input/dist/index.css';
import { ComponentProps, createContext, useCallback, use, useEffect, useMemo, useState } from 'react';
import { addBreadcrumb } from '@commutifi-fe/logger';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import en from 'world_countries_lists/data/countries/en/world.json';
import config from 'config/index';
import fontStyles from 'constants/fonts';
import { colors, screenSizes, spacing, themeVariables } from 'constants/global';
import { CurrencyCode, Locales } from 'constants/settings';
import { localSettingsActions } from 'store/modules/localSettings';
import { theme } from '@commutifi-fe/ui';
import 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/src/cookieconsent';
import 'vanilla-cookieconsent/src/cookieconsent.css';
import { AVAILABLE_LANGUAGES, LocalesType } from '@commutifi-fe/constants';
import { Themes } from '@commutifi-fe/ui/components/theme/constants';
import { useThemeDetector } from '@commutifi-fe/hooks';
type ContinentType = 'Europe' | 'America' | 'Asia' | 'Africa';
interface LocalConfigProviderValue {
  locale: LocalesType;
  isLoadingTranslations: boolean;
  currency: CurrencyCode;
  localeOptions: typeof AVAILABLE_LANGUAGES;
  changeLocale: (locale: LocalesType | string) => localSettingsActions.ChangeLocaleAction;
  isMenuAvailable: boolean;
  toggleShowMenu: () => void;
  showMenu: () => void;
  hideMenu: () => void;
  region: ContinentType;
  cookieconsent: CookieConsent | null;
  stripeOptions: ComponentProps<typeof Elements>['options'];
  theme: {
    currentTheme: `${Themes}`;
    isSystem: boolean;
  };
  changeTheme: (theme: `${AppThemes}`) => void;
}
interface LocaleConfigProviderProps {
  locale: LocalesType;
  children: React.ReactNode;
  isLoadingTranslations: boolean;
  changeTheme: (theme: `${AppThemes}`) => void;
  currentTheme: `${AppThemes}` | undefined;
}
const {
  useToken
} = theme;
const stripeLocaleMapping = new Map<LocalesType, StripeElementLocale>([[Locales.cs, 'cs'], [Locales.deDE, 'de'], [Locales.enAU, 'en-AU'], [Locales.enCA, 'en-CA'], [Locales.enGB, 'en-GB'], [Locales.enPH, 'en'], [Locales.enTH, 'en'], [Locales.enUS, 'en'], [Locales.enVI, 'en'], [Locales.esES, 'es-ES'], [Locales.esCL, 'es-ES'], [Locales.esCO, 'es-ES'], [Locales.esMX, 'es-ES'], [Locales.esUS, 'es-ES'], [Locales.frBE, 'fr'], [Locales.frFR, 'fr'], [Locales.frCA, 'fr-CA'], [Locales.itIT, 'it-IT'], [Locales.nlBE, 'nl'], [Locales.nlNL, 'nl'], [Locales.pl, 'pl'], [Locales.ptPT, 'pt']]);
const antdPhoneInputLocaleMapping = new Map<LocalesType, () => Promise<any>>([[Locales.cs, () => import('world_countries_lists/data/countries/cs/world.json')], [Locales.deDE, () => import('world_countries_lists/data/countries/de/world.json')], [Locales.enAU, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enCA, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enGB, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enPH, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enTH, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enUS, () => new Promise(resolve => {
  resolve(en);
})], [Locales.enVI, () => new Promise(resolve => {
  resolve(en);
})], [Locales.esES, () => import('world_countries_lists/data/countries/es/world.json')], [Locales.esCL, () => import('world_countries_lists/data/countries/es/world.json')], [Locales.esCO, () => import('world_countries_lists/data/countries/es/world.json')], [Locales.esMX, () => import('world_countries_lists/data/countries/es/world.json')], [Locales.esUS, () => import('world_countries_lists/data/countries/es/world.json')], [Locales.frBE, () => import('world_countries_lists/data/countries/fr/world.json')], [Locales.frCA, () => import('world_countries_lists/data/countries/fr/world.json')], [Locales.itIT, () => import('world_countries_lists/data/countries/it/world.json')], [Locales.nlBE, () => import('world_countries_lists/data/countries/nl/world.json')], [Locales.nlNL, () => import('world_countries_lists/data/countries/nl/world.json')], [Locales.pl, () => import('world_countries_lists/data/countries/pl/world.json')], [Locales.ptPT, () => import('world_countries_lists/data/countries/pt/world.json')]]);
export const ConfigContext = createContext<LocalConfigProviderValue | null>(null);

// Gracefully handle Stripe.js loading failures (network blocks, ad blockers, etc.)
// Returns null if Stripe fails to load - components should handle this gracefully
const loadStripeWithFallback = async (): Promise<Stripe | null> => {
  try {
    const stripe = await loadStripe(config.stripePk, {
      betas: ['us_bank_account_beta_2']
    });
    return stripe;
  } catch (error) {
    // Log breadcrumb for debugging but don't throw - Stripe will be unavailable
    addBreadcrumb('Stripe.js failed to load - payment features will be unavailable', 'warning', {
      error: error instanceof Error ? error.message : 'Unknown error',
      stripePkPrefix: config.stripePk?.substring(0, 10) // Log prefix to verify key exists
    });
    return null;
  }
};
export const stripePromise = loadStripeWithFallback() as ComponentProps<typeof Elements>['stripe'];
export enum AppThemes {
  DARK = 'dark',
  LIGHT = 'light',
  SYSTEM = 'system',
}
export function LocalConfigProvider(t0) {
  const $ = _c(68);
  const {
    children,
    locale,
    isLoadingTranslations,
    changeTheme,
    currentTheme
  } = t0;
  const dispatch = useDispatch();
  let t1;
  if ($[0] !== dispatch) {
    t1 = l => dispatch(localSettingsActions.changeLocale(l));
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const changeLocale = t1;
  const [phoneIntl, setPhoneIntl] = useState(en);
  let t2;
  let t3;
  if ($[2] !== locale) {
    t2 = () => {
      const lazyPhoneIntl = antdPhoneInputLocaleMapping.get(locale);
      if (lazyPhoneIntl && !locale.includes("en")) {
        lazyPhoneIntl().then(module => {
          setPhoneIntl(module.default);
        }).catch(_temp);
      } else {
        setPhoneIntl(en);
      }
    };
    t3 = [locale];
    $[2] = locale;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      maxWidth: screenSizes.mdMaxWidth
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const isMdDown = useMediaQuery(t4);
  const [isMenuAvailable, setIsMenuAvailable] = useState(isMdDown);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsMenuAvailable(_temp2);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const toggleShowMenu = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsMenuAvailable(true);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const showMenu = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsMenuAvailable(false);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const hideMenu = t7;
  const intlDateTimeFormat = Intl.DateTimeFormat;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = intlDateTimeFormat().resolvedOptions().timeZone.split("/");
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const region = t8[0] as ContinentType;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => region === "Europe" ? window.initCookieConsent() : null;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const [cc] = useState(t9);
  const {
    token
  } = useToken();
  let t10;
  if ($[11] !== locale) {
    t10 = stripeLocaleMapping.get(locale) || "en";
    $[11] = locale;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== token.feedbackDestructive500 || $[14] !== token.neutral500 || $[15] !== token.neutral600 || $[16] !== token.primary300 || $[17] !== token.primary400) {
    t11 = {
      colorPrimary: token.primary400,
      borderRadius: themeVariables.borderRadiusSmall,
      colorPrimaryText: token.neutral600,
      colorText: token.neutral600,
      colorTextPlaceholder: token.neutral500,
      colorTextSecondary: token.neutral500,
      colorDanger: token.feedbackDestructive500,
      colorDangerText: token.feedbackDestructive500,
      spacingGridColumn: spacing.medium2,
      spacingGridRow: spacing.large,
      colorWarning: "#ffc000",
      colorWarningText: "#ffc000",
      focusOutline: token.primary300,
      focusBoxShadow: `0px 0px 2px -1px ${colors.boxShadowColor}, 0px 1px 4px -2px ${colors.boxShadowColor}`,
      fontFamily: " Segma, sans-serif",
      fontSizeBase: "14px",
      fontLineHeight: "1.57"
    };
    $[13] = token.feedbackDestructive500;
    $[14] = token.neutral500;
    $[15] = token.neutral600;
    $[16] = token.primary300;
    $[17] = token.primary400;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== token.neutral600) {
    t12 = {
      fontWeight: themeVariables.fontWeightMedium,
      fontSize: "var(--fontSizeBase)",
      marginBottom: spacing.small,
      color: token.neutral600
    };
    $[19] = token.neutral600;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== token.colorBgContainer || $[22] !== token.neutral600) {
    t13 = {
      padding: `11px ${spacing.small3}`,
      color: token.neutral600,
      backgroundColor: token.colorBgContainer
    };
    $[21] = token.colorBgContainer;
    $[22] = token.neutral600;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      boxShadow: `0px 0px 2px -1px ${colors.boxShadowColor}, 0px 1px 4px -2px ${colors.boxShadowColor}`
    };
    t15 = {
      boxShadow: `0px 0px 2px -1px ${colors.boxShadowColor}, 0px 1px 4px -2px ${colors.boxShadowColor}`,
      borderColor: colors.disabled
    };
    $[24] = t14;
    $[25] = t15;
  } else {
    t14 = $[24];
    t15 = $[25];
  }
  let t16;
  if ($[26] !== token.primary300) {
    t16 = {
      boxShadow: `0px 0px 2px -1px ${colors.boxShadowColor}, 0px 1px 4px -2px ${colors.boxShadowColor}`,
      borderColor: token.primary300
    };
    $[26] = token.primary300;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      fontSize: "var(--fontSizeBase)"
    };
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  let t19;
  if ($[29] !== token.colorBgContainer) {
    t18 = {
      backgroundColor: token.colorBgContainer
    };
    t19 = {
      backgroundColor: token.colorBgContainer
    };
    $[29] = token.colorBgContainer;
    $[30] = t18;
    $[31] = t19;
  } else {
    t18 = $[30];
    t19 = $[31];
  }
  let t20;
  if ($[32] !== token.colorBgElevated) {
    t20 = {
      backgroundColor: token.colorBgElevated,
      color: "white"
    };
    $[32] = token.colorBgElevated;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t16 || $[37] !== t18 || $[38] !== t19 || $[39] !== t20) {
    t21 = {
      ".Label": t12,
      ".Input": t13,
      ".Input:hover, .Input--invalid": t14,
      ".Input:hover": t15,
      ".Input:hover:focus, .Input:focus": t16,
      ".Error": t17,
      ".Tab": t18,
      ".PickerItem": t19,
      ".PickerItem:hover": t20
    };
    $[34] = t12;
    $[35] = t13;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t11 || $[42] !== t21) {
    t22 = {
      variables: t11,
      rules: t21
    };
    $[41] = t11;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t10 || $[45] !== t22) {
    t23 = {
      fonts: fontStyles,
      locale: t10,
      appearance: t22
    };
    $[44] = t10;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const stripeOptions = t23;
  const detectedTheme = useThemeDetector();
  const t24 = currentTheme === AppThemes.SYSTEM ? detectedTheme === "dark" ? Themes.DARK : Themes.LIGHT : currentTheme || Themes.LIGHT;
  const t25 = currentTheme === AppThemes.SYSTEM;
  let t26;
  if ($[47] !== t24 || $[48] !== t25) {
    t26 = {
      currentTheme: t24,
      isSystem: t25
    };
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== cc || $[51] !== changeLocale || $[52] !== changeTheme || $[53] !== isLoadingTranslations || $[54] !== isMenuAvailable || $[55] !== locale || $[56] !== stripeOptions || $[57] !== t26) {
    t27 = {
      locale,
      isLoadingTranslations,
      currency: CurrencyCode.USD,
      localeOptions: AVAILABLE_LANGUAGES,
      changeLocale,
      isMenuAvailable,
      toggleShowMenu,
      showMenu,
      hideMenu,
      region,
      cookieconsent: cc,
      stripeOptions,
      changeTheme,
      theme: t26
    };
    $[50] = cc;
    $[51] = changeLocale;
    $[52] = changeTheme;
    $[53] = isLoadingTranslations;
    $[54] = isMenuAvailable;
    $[55] = locale;
    $[56] = stripeOptions;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const value = t27;
  let t28;
  if ($[59] !== children || $[60] !== stripeOptions) {
    t28 = <Elements stripe={stripePromise} options={stripeOptions}>{children}</Elements>;
    $[59] = children;
    $[60] = stripeOptions;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== phoneIntl || $[63] !== t28) {
    t29 = <PhoneConfigProvider locale={phoneIntl}>{t28}</PhoneConfigProvider>;
    $[62] = phoneIntl;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t29 || $[66] !== value) {
    t30 = <ConfigContext value={value}>{t29}</ConfigContext>;
    $[65] = t29;
    $[66] = value;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  return t30;
}
function _temp2(previous) {
  return !previous;
}
function _temp() {}
export const useConfig = () => {
  const context = use(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
export default LocalConfigProvider;